// # Toast — 화면 구석에 잠깐 떴다 사라지는 한 줄(docs/design.md)
//
// 알림 더미는 컴포넌트가 아니라 **가게**(`ToastStore`)가 든다. 부르는 쪽은 어디서든 `show` 한 번이면
// 되고, 그리는 쪽(`Toaster`)은 셸에 하나만 둔다. 가게를 모듈 전역 하나로 두지 않고 만들어 쓰게 한
// 까닭은 시험이다 — 시험마다 새 가게를 쥐면 앞 시험의 알림이 다음 시험에 새지 않는다.
//
// - 스크린리더에는 `role="status"`(공손)로, 오류(`danger`)만 `role="alert"`(끼어듦)로 읽힌다.
// - 쌓이는 수는 `TOAST_MAX`(3). 넘치면 **가장 오래된 것**이 빠진다 — 새 소식이 밀려나면 안 된다.
// - 저절로 닫히는 시간은 5초, 오류는 8초, **단추가 달린 알림은 15초**다. 마우스를 올리거나 초점이 안에
//   있으면 시계를 멈추고, 풀리면 처음부터 다시 잰다 — 누를 사람에게는 시간이 끝나지 않는다. 한때 단추가
//   달리면 아예 안 닫았는데, 북마크 둘을 지운 "되돌리기" 두 장이 채널 머리의 도구를 몇 분이고 덮었다(3차 F273).
// - 판은 `popover="manual"` 로 맨 위 층에 올린다. 새 알림이 오면 다시 올려 열린 창보다 위에 선다.
// - 셸(`Shell`)이 가게 하나를 `ToastContext` 로 내려 주고, 화면은 `useToasts()` 로 쥔다. 셸 밖(시험)에서는
//   아무도 그리지 않는 빈 가게가 나온다 — 알림은 덤이라, 없다고 동작이 멈추면 안 된다.
// - 그리는 쪽(`Toaster`)은 `toaster.tsx` 에 따로 산다. 셸은 첫 알림이 뜰 때 그것을 받는다 — 가게와
//   컨텍스트만 첫 청크에 싣는다.

import { createContext, type ReactNode, useContext } from 'react'

export type ToastTone = 'neutral' | 'info' | 'ok' | 'danger' | 'warn'

export interface ToastInput {
  title: ReactNode
  description?: ReactNode
  /** 기본 `neutral`. `danger` 면 `role="alert"`. */
  tone?: ToastTone
  /** 단추 하나(예: "되돌리기"). 누르면 알림도 닫힌다. */
  action?: { label: string; on_click: () => void }
  /** 저절로 닫히기까지(ms). `null` 이면 닫지 않는다. 기본은 `toast_duration`. */
  duration?: number | null
}

export interface ToastItem extends ToastInput {
  id: number
}

export const TOAST_MAX = 3
export const TOAST_MS = 5000
export const TOAST_DANGER_MS = 8000
/** 단추(되돌리기 등)가 달린 알림. 읽고 손을 옮길 틈 — 올리거나 초점을 두면 멈춘다. */
export const TOAST_ACTION_MS = 15000

/** 저절로 닫히기까지. 단추가 있으면 더 길게. `null` 은 부르는 쪽이 `duration: null` 로 청했을 때뿐이다. */
export function toast_duration(item: ToastInput): number | null {
  if (item.duration !== undefined) return item.duration
  if (item.action) return TOAST_ACTION_MS
  return item.tone === 'danger' ? TOAST_DANGER_MS : TOAST_MS
}

/** 더미에 하나 얹기. 넘치면 오래된 것부터 뺀다. */
export function toast_push(list: readonly ToastItem[], item: ToastItem, max = TOAST_MAX) {
  const next = [...list, item]
  return next.length > max ? next.slice(next.length - max) : next
}

export interface ToastStore {
  /** 알림을 띄우고 번호를 돌려준다(나중에 `dismiss` 로 닫을 때). */
  show: (input: ToastInput) => number
  dismiss: (id: number) => void
  list: () => readonly ToastItem[]
  subscribe: (listen: () => void) => () => void
}

export function createToastStore(max = TOAST_MAX): ToastStore {
  let items: readonly ToastItem[] = []
  let next_id = 1
  const ears = new Set<() => void>()
  const tell = () => {
    for (const listen of ears) listen()
  }
  return {
    show(input) {
      const id = next_id++
      items = toast_push(items, { ...input, id }, max)
      tell()
      return id
    },
    dismiss(id) {
      const kept = items.filter((it) => it.id !== id)
      if (kept.length === items.length) return
      items = kept
      tell()
    },
    list: () => items,
    subscribe(listen) {
      ears.add(listen)
      return () => ears.delete(listen)
    },
  }
}

const quiet = createToastStore()

/** 셸이 내려 주는 알림 가게. */
export const ToastContext = createContext<ToastStore>(quiet)

/** 지금 셸의 알림 가게. 셸 밖이면 아무도 그리지 않는 가게다. */
export function useToasts(): ToastStore {
  return useContext(ToastContext)
}
